import type { Shape } from "plotly.js-dist-min";

import type { DataFrameDto } from "@/components/datascience/DataFrameWidget.vue";
import { type PlotlyWidgetSpec } from "@/components/datascience/PlotlyWidget.vue";

export interface InspectionDataframe extends DataFrameDto {
  method: string;
  verboseMethodName: string;
  datasource?: string;
}

const MAX_LABEL_LENGTH = 20;

function truncateLabel(label: string, maxLength: number = MAX_LABEL_LENGTH): string {
  if (label.length <= maxLength) return label;
  return label.slice(0, maxLength - 1) + "…";
}

export function makeBoxPlot(
  dataframes: InspectionDataframe[],
  plotTitle: string = "",
  xAxisLabelPermutationImportance: string,
  tracePrefix: string = ""
) {
  let featureCount = 0;
  const traces = [];
  for (const [i, df] of dataframes.entries()) {
    let featureNames = df.index.map((a) => a[1]);
    featureCount = featureNames.length;
    if (featureNames.every((i) => i === undefined)) {
      featureNames = Array.from({ length: df.index.length }, (_, i) => `Feature #${i}`);
    }
    const truncatedNames = featureNames.map((n) => truncateLabel(n));
    const x = df.data.flat();
    const y = truncatedNames.flatMap((name, i) => Array(df.data[i]?.length ?? 0).fill(name));
    const customdata = featureNames.flatMap((name, i) => Array(df.data[i]?.length ?? 0).fill(name));
    traces.push({
      x,
      y,
      customdata,
      type: "box",
      boxpoints: false,
      orientation: "h",
      name: tracePrefix !== "" ? `${tracePrefix}#${i}` : undefined,
      hovertemplate: "<b>%{customdata}</b><br>Value: %{x}<extra></extra>",
    });
  }
  const shapes = Array.from({ length: featureCount }, (_, i) => ({
    type: "rect",
    xref: "paper",
    yref: "y",
    x0: 0,
    x1: 1,
    y0: -0.5 + i,
    y1: 0.5 + i,
    fillcolor: i % 2 === 0 ? "rgba(0, 0, 0, 0.03)" : "rgba(0, 0, 0, 0)",
    line: { width: 0 },
  }));
  const leftMargin = MAX_LABEL_LENGTH * 16 * 0.6 + 10;
  return {
    data: traces,
    layout: {
      shapes,
      margin: { t: 30, l: leftMargin, b: 50, r: 0 },
      showlegend: dataframes.length > 1,
      xaxis: {
        showgrid: false,
        zeroline: true,
        title: {
          text: xAxisLabelPermutationImportance,
          font: {
            family: "GeistMono, monospace",
            size: 14,
            color: "#2f3037",
          },
        },
      },
      yaxis: {
        showgrid: true,
        tickfont: {
          family: "GeistMono, monospace",
          size: 16,
          color: "#2f3037",
        },
      },
      title: {
        text: plotTitle,
      },
      boxmode: dataframes.length > 1 ? "group" : undefined,
    },
    frames: [],
  } as PlotlyWidgetSpec;
}

export function makeBarplot(
  dataframes: InspectionDataframe[],
  plotTitle: string = "",
  tracePrefix: string = ""
) {
  const traces = [];
  for (const [i, df] of dataframes.entries()) {
    const featureNames = df.index.map((idx) => idx.toString());
    const truncatedNames = featureNames.map((n) => truncateLabel(n));
    traces.push({
      x: df.data.flat(),
      y: truncatedNames,
      customdata: featureNames,
      type: "bar",
      orientation: "h",
      name: tracePrefix !== "" ? `${tracePrefix}#${i}` : undefined,
      hovertemplate: "<b>%{customdata}</b><br>Value: %{x}<extra></extra>",
    });
  }
  const shapes: Shape[] = [];
  const first = traces[0];
  if (first) {
    first.x.map((_, i) => ({
      type: "rect",
      xref: "paper",
      yref: "y",
      x0: 0,
      x1: 1,
      y0: -0.5 + i,
      y1: 0.5 + i,
      fillcolor: i % 2 === 0 ? "rgba(0, 0, 0, 0.03)" : "rgba(0, 0, 0, 0)",
      line: { width: 0 },
    }));
  }
  const leftMargin = MAX_LABEL_LENGTH * 16 * 0.6 + 10;
  return {
    data: traces,
    layout: {
      shapes,
      margin: { t: 30, l: leftMargin, b: 50, r: 35 },
      xaxis: {
        title: {
          text: "Model coefficient value",
          font: {
            family: "Geist, sans-serif",
            size: 14,
            color: "#2f3037",
          },
        },
      },
      yaxis: {
        tickfont: {
          family: "GeistMono, monospace",
          size: 16,
          color: "#2f3037",
        },
      },
      title: {
        text: plotTitle,
      },
    },
    frames: [],
  } as PlotlyWidgetSpec;
}
